import React from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import {
  BadgeAlert,
  Briefcase,
  Eye,
  GraduationCap,
  MapPin,
  RefreshCw,
  Settings,
  Share2,
  Star,
} from "lucide-react";
import { MatchListEntry } from "../../../types";
import { timeAgo } from "../../../helpers/function.helper";
import { UPLOAD_URL } from "../../../utils/constants";
import { getGenderPlaceholder } from "../../../helpers/matches.helper";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";
import { InfoRow } from "./InfoRow";

const GridListItem = ({
  match,
  onAddToShortlist,
  matches,
  setProfiles,
  onViewProfile,
  onSwitchProfile,
  onManageProfile,
  onShareProfile,
  onReportProfile,
}: {
  match: MatchListEntry;
  onAddToShortlist?: Function;
  matches?: any[];
  setProfiles?: Function;
  onSwitchProfile?: (match: MatchListEntry) => void;
  onManageProfile?: (match: MatchListEntry) => void;
  onViewProfile: (match: MatchListEntry) => void;
  onShareProfile?: (match: MatchListEntry) => void;
  onReportProfile?: (match: MatchListEntry) => void;
}) => {
  const location = useLocation();
  const isOtherPage = location.pathname.includes("other");
  const { language, t } = useLanguage();
  const scoreLabel = t("matches.labels.score");
  const publicLabel = t("matches.labels.public");
  const viewsTemplate = t("matches.labels.viewsCount");
  const lastSeenPrefix = t("matches.labels.lastSeen");
  const byLabel = t("matches.labels.by");
  const viewLabel = t("matches.buttons.view");
  const switchLabel = t("matches.buttons.switch");
  const manageLabel = t("matches.buttons.manage");
  const shareLabel = t("matches.buttons.share");
  const reportLabel = t("matches.buttons.report");

  const viewsLabel = viewsTemplate.replace("{count}", String(match.views ?? 0));
  const lastSeenLabel = `${lastSeenPrefix}: ${timeAgo(
    match.updatedAt,
    language
  )}`;
  const isShortlisted = Boolean(match.isShortlisted);
  const blurPhoto = shouldBlurProfileImage(match.imagePrivacy);
  return (
    <div key={match.id} className="space-y-3">
      <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group">
        <div className="relative h-80 overflow-hidden">
          <img
            src={UPLOAD_URL + "/users/profile_pic/" + match.image}
            alt={match.fullName}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 object-top ${
              blurPhoto ? "blur-sm scale-110 brightness-90" : ""
            }`}
            onError={(event) => {
              (event.currentTarget as HTMLImageElement).src =
                getGenderPlaceholder(match.gender);
            }}
          />

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onAddToShortlist?.(match, matches, setProfiles);
            }}
            aria-label="Shortlist profile"
            className={`absolute top-2 left-2 flex items-center justify-center rounded-full p-1.5 shadow-md transition ${
              isShortlisted
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "bg-white/90 text-orange-500 hover:bg-white"
            }`}
          >
            {isShortlisted ? (
              <Star className="w-4 h-4" fill="currentColor" />
            ) : (
              <Star className="w-4 h-4" />
            )}
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <h3 className="text-xl font-bold text-white">
              {match.fullName}
              {" | "}
              <span className="text-sm font-bold">{match.maritalId}</span>
            </h3>
            <p className="text-sm text-white/90">
              {match.age}, {match.height}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-2 px-4 pt-5">
          <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
            {scoreLabel}: {match.profileScore}%
          </span>
          <div className="flex items-center gap-1.5 bg-green-100 px-2 py-1 rounded-full">
            <div className="w-2 h-2 bg-green-600 rounded-full"></div>

            <span className="text-xs font-semibold text-green-700">
              {publicLabel}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-green-600">
              <Eye size={16} />
              <span className="text-sm font-semibold">{viewsLabel}</span>
            </div>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-700 leading-relaxed space-y-1 px-4">
          <p className="flex flex-wrap gap-x-2 gap-y-1">
            {match.maritalStatus && <span>{match.maritalStatus}</span>}
            {match.caste && <span>{match.caste}</span>}
            {match.annualIncome && <span>{match.annualIncome}</span>}
          </p>
        </div>
        <div className="px-4 py-4">
          <div className="space-y-2 mb-4">
            {/* @ts-ignore */}
            <InfoRow icon={Briefcase} text={match.occupation ?? ""} compact />
            {/* @ts-ignore */}
            <InfoRow
              icon={GraduationCap}
              text={match.education ?? ""}
              compact
            />
            {/* @ts-ignore */}
            <InfoRow
              icon={MapPin}
              text={`${match.district ?? ""}, ${match.state ?? ""}`}
              compact
            />
          </div>

          <div className="text-xs text-gray-500 flex justify-between items-center">
            <span>
              {byLabel}: {match.createdBy}
            </span>
            <span>{lastSeenLabel}</span>
          </div>
        </div>
        <div className="flex w-full gap-2 p-2 border-t border-gray-100">
          <button
            onClick={() => onViewProfile?.(match)}
            className="flex w-full items-center justify-center py-2 px-3 gap-2 rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition"
          >
            <Eye size={18} />
            <span className="font-semibold text-sm">{viewLabel}</span>
          </button>
          {/* {!isOtherPage && (
            <>
              <div className="relative">
                <button
                  className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSwitchProfile?.(match);
                  }}
                >
                  <RefreshCw size={14} />
                  <span>{switchLabel}</span>
                </button>

                {match?.switchCount ? (
                  <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-semibold text-white">
                    {match?.switchCount}
                  </span>
                ) : null}
              </div>

              <button
                className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                onClick={(event) => {
                  event.stopPropagation();
                  onManageProfile?.(match);
                }}
              >
                <Settings size={14} />
                <span className="font-semibold text-sm">{manageLabel}</span>
              </button>
            </>
          )} */}
          <button
            className="flex w-full justify-center items-center gap-1 p-2 rounded-lg border border-purple-200 text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              onShareProfile?.(match);
            }}
          >
            <Share2 size={20} />
            <span className="font-semibold text-sm">{shareLabel}</span>
          </button>
          {isOtherPage && (
            <button
              className="px-4 py-2 border border-gray-300 bg-red-400 rounded-lg flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm hover:bg-red-500 text-white transition"
              onClick={(event) => {
                event.stopPropagation();
                onReportProfile?.(match);
              }}
            >
              <BadgeAlert size={14} />
              <span>{reportLabel}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridListItem;
